import { useTranslation } from 'react-i18next';
import { calloutOutline } from '@peruselab/structure-editor/rendering/calloutGeometry';
import type { CalloutBubble } from '@peruselab/structure-editor/model';
import type { EditorTool } from '@peruselab/structure-editor/domain/editor';

export function CalloutGlyph({ variant = 'rounded' }: { readonly variant?: CalloutBubble['variant'] }) {
  return (
    <svg viewBox="0 0 32 28" width="24" height="24" aria-hidden="true">
      <path
        d={calloutOutline(
          { x: 3, y: 3 },
          { x: 29, y: 20 },
          variant === 'leader'
            ? { x: 31, y: 2 }
            : variant === 'chevron' || variant === 'notched-arrow'
              ? { x: 32, y: 11.5 }
              : { x: 7, y: 26 },
          variant
        )}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
      />
    </svg>
  );
}

export function CalloutsGroup({
  tool,
  setTool,
  open,
  onToggle
}: {
  readonly tool: EditorTool;
  readonly setTool: (tool: EditorTool) => void;
  readonly open: boolean;
  readonly onToggle: () => void;
}) {
  const { t } = useTranslation();
  return (
    <details id="rail-group-callouts" className="workspace-rail-annotations" open={open}>
      <summary
        onClick={(event) => {
          event.preventDefault();
          onToggle();
        }}
      >
        {t('workspace.rail.shapes.callout.groupLabel')}
      </summary>
      <div className="workspace-rail-annotations-body">
        <div
          className="workspace-rail-annotations-grid"
          role="group"
          aria-label={t('workspace.rail.shapes.callout.groupLabel')}
        >
          {(['rect', 'rounded', 'ellipse', 'cloud', 'burst', 'leader', 'chevron', 'notched-arrow'] as const).map(
            (variant) => {
              const active = tool === `callout-${variant}`;
              const label = t(`workspace.rail.shapes.callout.${variant}`);
              return (
                <button
                  key={`callout-${variant}`}
                  type="button"
                  className={`icon-button ${active ? 'active' : ''}`}
                  aria-label={label}
                  title={label}
                  aria-pressed={active}
                  onClick={() => setTool(active ? 'select' : `callout-${variant}`)}
                >
                  <CalloutGlyph variant={variant} />
                </button>
              );
            }
          )}
        </div>
        <p className="workspace-rail-annotations-hint">{t('workspace.rail.shapes.callout.hint')}</p>
      </div>
    </details>
  );
}
